<template>
  <div class="release-notes-wrapper">
    <div class="_header">
      <m-btn
        class="_changelog"
        :href="releaseNotesLink"
        target="_blank"
        hide-border
        xs
      >
        {{ $t('releaseNotesWrapper.changelog') }}
        <m-icon
          type="lucide-arrow-up-right"
          :style="{ marginLeft: '.4rem' }"
        />
      </m-btn>
      <m-btn
        class="_close"
        fab
        xs
        light
        icon="close"
        hide-border
        :aria-label="$t('general.close')"
        @click="$emit('close')"
      />
    </div>
    <div class="_body">
      <div
        v-for="releaseNote in releaseNotes"
        :key="releaseNote._id"
        :class="['_entry', { '-unread': unreadReleaseNoteIds.includes(releaseNote._id) }]"
      >
        <div class="_date">
          {{ formatDate(releaseNote.releaseDate) }}
        </div>
        <release-note-entry :release-note="releaseNote" />
      </div>
      <m-spinner
        v-if="loading"
        class="_loading"
        size="sm"
      />
      <div
        v-else-if="failed"
        class="_failed"
      >
        {{ $t('releaseNotesWrapper.loadFailed') }}
      </div>
      <div
        v-else-if="empty"
        class="_empty"
      >
        {{ $t('releaseNotesWrapper.empty') }}
      </div>
      <div class="_footer">
        <m-btn
          :href="releaseNotesLink"
          target="_blank"
          hide-border
          xs
        >
          {{ $t('releaseNotesWrapper.fullChangelog') }}
          <m-icon
            type="lucide-arrow-up-right"
            :style="{ marginLeft: '.4rem' }"
          />
        </m-btn>
      </div>
    </div>
  </div>
</template>

<script>
import ReleaseNoteEntry from '@/release-notes/ReleaseNoteEntry.vue';
import { DateTime } from 'luxon';
import { RELEASE_NOTES_STATE } from '@/composables/release-notes/release-notes';
import { environmentVariable, resolveEnvironmentVariable } from '@/lib/env';

export default {
  name: 'ReleaseNotesWrapper',
  props: {
    releaseNotes: {
      type: Array,
      required: true,
    },
    unreadReleaseNoteIds: {
      type: Array,
      default: () => [],
    },
    state: {
      type: String,
      required: true,
    },
  },
  components: { ReleaseNoteEntry },
  emits: ['close'],
  computed: {
    loading() {
      return this.releaseNotes.length === 0
        && [RELEASE_NOTES_STATE.idle, RELEASE_NOTES_STATE.loading].includes(this.state);
    },
    failed() {
      return this.releaseNotes.length === 0 && this.state === RELEASE_NOTES_STATE.failed;
    },
    empty() {
      return this.releaseNotes.length === 0 && this.state === RELEASE_NOTES_STATE.loaded;
    },
    releaseNotesLink() {
      return this.$t('releaseNotesWrapper.releaseNotesLink', { baseURL: resolveEnvironmentVariable(environmentVariable.WEBSITE_URL) });
    },
  },
  methods: {
    formatDate(date) {
      return DateTime.fromISO(date).toLocaleString(DateTime.DATE_MED);
    },
  },
  watch: {
    $route() {
      this.$emit('close');
    },
  },
};
</script>

<style
  scoped
  lang="scss"
  type="text/scss"
>
  .release-notes-wrapper {
    display: grid;
    grid-template-rows: auto 1fr;
    grid-template-columns: 1fr;
    height: calc(var(--viewport-height-100) - 3.2rem);

    ._header {
      display: grid;
      grid-template-columns: 1fr auto 1fr;
      align-items: center;
      padding: .8rem .8rem .8rem 1.2rem;
      border-bottom: 1px solid $input-border-color;

      ._changelog {
        grid-column: 2;
        justify-self: center;
      }

      ._close {
        justify-self: end;
      }
    }

    ._body {
      padding: map_get($padding, '12');
      overflow: auto;
      scrollbar-gutter: stable;

      ._entry + ._entry {
        margin-top: 6.4rem;
      }

      ._date {
        position: relative;
        margin-bottom: .8rem;
        color: $font-color-secondary;
      }

      ._entry.-unread ._date::before {
        position: absolute;
        top: 50%;
        left: -2rem;
        width: 1rem;
        height: 1rem;
        margin-top: -.5rem;
        content: "";
        background-color: $primary-color;
        border-radius: 50%;
      }

      ._loading {
        display: flex;
        justify-content: center;
      }

      ._empty,
      ._failed {
        color: $font-color-secondary;
        text-align: center;
      }

      ._footer {
        display: flex;
        justify-content: center;
        margin-top: 4.8rem;
      }
    }
  }
</style>
